import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import Alert from "../Alert";
import { sendGetRequest, sendPostRequest } from "../../utils";
import EmployeeFilters from "./EmployeeFilters";
import { EmployeeStatus } from "../../enums/employeeStatus";
import { Button } from "@mui/material";
import { compareBy } from "../../lib/helpers";
import EmployeeForm from "./EmployeeForm";
import moment from "moment-timezone";
import EmployeePoints from "./EmployeePoints";
import EmployeeRewardForm from "./EmployeeRewardForm";
import EmployeeRewardClaimed from "./EmployeeRewardClaimed";

moment.tz.setDefault("America/Toronto");

function Employee() {
  const [data, setData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filterByStatus, setFilterByStatus] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [employeePoints, setEmployeePoints] = useState({
    open: false,
    employee: null,
    filters: {
      start_date: moment().startOf("isoWeek").toISOString(),
      end_date: moment().endOf("isoWeek").toISOString(),
    },
  });
  const [claimedRewards, setClaimedRewards] = useState({
    open: false,
    employee: null,
  });
  const [employeeReward, setEmployeeReward] = useState({
    open: false,
    employee: null,
  });
  const statuses = Object.values(EmployeeStatus);
  const statusOptions = statuses.map((status) => ({
    value: status,
    label:
      status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " "),
  }));

  const doFetchEmployees = () => {
    sendGetRequest(
      `employees?start_date=${moment()
        .startOf("isoWeek")
        .toISOString()}&end_date=${moment().endOf("isoWeek").toISOString()}`,
      true
    )
      .then((res) => res.text())
      .then((data) => {
        setData(JSON.parse(data));
        setFilteredData(JSON.parse(data));
      })
      .catch((err) => setError(JSON.stringify(err)));
  };

  useEffect(() => {
    doFetchEmployees();
  }, []);

  useEffect(() => {
    let newFilterData = data;

    if (filterByStatus !== "") {
      newFilterData = newFilterData.filter((x) => x.status === filterByStatus);
    }

    if (search !== "") {
      newFilterData = newFilterData.filter(
        (x) =>
          x.last_name.toLowerCase().includes(search.toLowerCase()) ||
          x.first_name.toLowerCase().includes(search.toLowerCase()) ||
          x.email.toLowerCase().includes(search.toLowerCase()) ||
          x.phone_number.toLowerCase().includes(search.toLowerCase())
      );
    }

    //Sort
    if (newFilterData.length) {
      newFilterData = newFilterData
        .slice()
        .sort((a, b) => compareBy(a, b, sortBy));
    }

    setFilteredData(newFilterData);
  }, [data, sortBy, filterByStatus, search]);

  const doSetFilterByStatus = (filter) => {
    setFilterByStatus(filter);
  };

  const doSetStatus = (id, status) => {
    sendPostRequest(`employees/update-status/${id}`, { status })
      .then((res) => res.text())
      .then((data) => {
        setSuccess("Status updated successfully.");
        doFetchEmployees();
      })
      .catch((err) => setError(JSON.stringify(err)));
  };

  const generateStatusClass = (status) => {
    if (status === EmployeeStatus.ACTIVE) {
      return "bg-green-100 text-green-700";
    } else if (status === EmployeeStatus.ARCHIVED) {
      return "bg-gray-50 text-gray-700";
    } else {
      return "bg-red-100 text-red-700";
    }
  };

  const doSetSortBy = (sort) => {
    setSortBy(sort);
  };

  const doSetSearch = (search) => {
    setSearch(search);
  };

  const handleOpenForm = (row = null) => {
    setSelectedEmployee(row);
    setShowForm(true);
  };

  const handleRedeemPoints = (row) => {
    setEmployeeReward((prev) => ({
      ...prev,
      open: true,
      employee: row,
    }));
  };

  return (
    <div>
      <Box sx={{ paddingTop: 2, flexGrow: 1 }} className="shadow-lg border">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert error={error} containerClassName="mt-0 mb-2" />
            <Alert success={success} containerClassName="mt-0 mb-2" />
            <TableContainer
              component={Paper}
              className="!border-none !shadow-none"
            >
              <Typography variant="h4" className="p-4 customer-list-header">
                Employees
              </Typography>
              <EmployeeFilters
                doSetFilterByStatus={doSetFilterByStatus}
                doSetSortBy={doSetSortBy}
                doSetSearch={doSetSearch}
                openForm={() => handleOpenForm(null)}
              />
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="!font-semibold">First Name</TableCell>
                    <TableCell className="!font-semibold">Last Name</TableCell>
                    <TableCell className="!font-semibold">Status</TableCell>
                    <TableCell className="!font-semibold">
                      Date Created
                    </TableCell>
                    <TableCell className="!font-semibold !text-center">
                      All Points
                    </TableCell>
                    <TableCell className="!font-semibold !text-center">
                      Pts (This Wk)
                    </TableCell>
                    <TableCell className="!font-semibold !text-center">
                      Used Pts
                    </TableCell>
                    <TableCell className="!font-semibold !text-center">
                      Avail. Pts
                    </TableCell>
                    <TableCell className="!font-semibold">&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData?.length > 0 ? (
                    <>
                      {filteredData.map((row) => {
                        return (
                          <TableRow
                            key={row.id}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell size="small">{row.first_name}</TableCell>
                            <TableCell size="small">{row.last_name}</TableCell>
                            <TableCell size="small">
                              <select
                                className={`border p-1 rounded-md border-gray-400 ${generateStatusClass(
                                  row.status
                                )}`}
                                onChange={(e) =>
                                  doSetStatus(row.id, e.target.value)
                                }
                                value={row.status}
                              >
                                {statusOptions.map((option) => (
                                  <option
                                    key={option.value}
                                    value={option.value}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </TableCell>
                            <TableCell size="small">
                              {moment(row.created_at).format("YYYY-MM-DD")}
                            </TableCell>
                            <TableCell size="small" className="!text-center">
                              {parseFloat(row.total_points).toLocaleString()}
                            </TableCell>
                            <TableCell size="small" className="!text-center">
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                className="!mx-auto"
                                onClick={() => {
                                  setEmployeePoints((prev) => ({
                                    ...prev,
                                    open: true,
                                    employee: row,
                                    filters: {
                                      start_date: moment()
                                        .startOf("isoWeek")
                                        .toISOString(),
                                      end_date: moment()
                                        .endOf("isoWeek")
                                        .toISOString(),
                                    },
                                  }));
                                }}
                              >
                                {parseFloat(row.period_points).toLocaleString()}
                              </Button>
                            </TableCell>
                            <TableCell size="small" className="!text-center">
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                className="!mx-auto"
                                onClick={() => {
                                  setClaimedRewards((prev) => ({
                                    ...prev,
                                    open: true,
                                    employee: row,
                                  }));
                                }}
                              >
                                {parseFloat(row.used_points).toLocaleString()}
                              </Button>
                            </TableCell>
                            <TableCell size="small" className="!text-center">
                              {parseFloat(
                                row.total_points - row.used_points
                              ).toLocaleString()}
                            </TableCell>
                            <TableCell
                              size="small"
                              className="!text-right !items-end justify-end"
                            >
                              <div className="flex gap-2 max-w-[210px] self-end ml-auto">
                                <Button
                                  type="button"
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleRedeemPoints(row)}
                                  color="primary"
                                >
                                  Redeem Points
                                </Button>
                                <Button
                                  type="button"
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleOpenForm(row)}
                                  color="primary"
                                >
                                  Edit
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <TableRow>
                        <TableCell colSpan={12} size="medium">
                          <div className="text-center">No record found.</div>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
      <EmployeeForm
        data={selectedEmployee}
        open={showForm}
        onClose={() => setShowForm(false)}
        doFetchEmployees={doFetchEmployees}
        setSuccess={setSuccess}
      />
      <EmployeePoints
        open={employeePoints.open}
        onClose={() => setEmployeePoints((prev) => ({ ...prev, open: false }))}
        employee={employeePoints.employee}
        filters={employeePoints.filters}
      />
      <EmployeeRewardForm
        open={employeeReward.open}
        onClose={() => setEmployeeReward((prev) => ({ ...prev, open: false }))}
        employee={employeeReward.employee}
        doFetchEmployees={doFetchEmployees}
      />
      <EmployeeRewardClaimed
        open={claimedRewards.open}
        onClose={() => setClaimedRewards((prev) => ({ ...prev, open: false }))}
        employee={claimedRewards.employee}
      />
    </div>
  );
}

export default Employee;
